import React from 'react';
import { Button } from '../ui/button';
import { Download, Shield, HardDrive, Lock, CheckCircle2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const DownloadSection = () => {
  const securityFeatures = [
    {
      icon: HardDrive,
      title: 'Local Storage Only',
      description: 'All your loadboard credentials are stored locally on your machine. We never see or store your passwords.',
    },
    {
      icon: Lock,
      title: 'Encrypted at Rest',
      description: 'Your data is encrypted using AES-256 encryption, the same standard used by banks.',
    },
    {
      icon: Shield,
      title: 'You Control Access',
      description: 'Delete your data anytime. Uninstall the app and everything is gone. No cloud storage, no traces.',
    },
  ];

  return (
    <section id="download" className="py-24 bg-slate-950 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-sky-500/10 text-sky-400 text-sm font-medium mb-4">
            Download
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Your Logins Stay
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-amber-400">
              {' '}On Your Machine
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Download LoadSurf AI and keep full control of your data. All credentials are stored 
            locally and encrypted — we never see your passwords.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Download Card */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-3xl p-8 relative text-center">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-sky-500/20 to-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <Download className="w-12 h-12 text-amber-400" />
            </div>

            <h3 className="text-2xl font-bold text-white mb-3">
              LoadSurf AI Desktop App
            </h3>
            
            <p className="text-slate-400 mb-8">
              Available for Windows 10+ and macOS (Apple Silicon & Intel)
            </p>

            <Button
              size="lg"
              className="w-full bg-gradient-to-r from-sky-500 to-amber-500 hover:from-sky-400 hover:to-amber-400 text-slate-950 font-semibold py-6 text-lg group"
              asChild
            >
              <Link to="/download">
                <Download className="mr-2 w-5 h-5" />
                Download Now
                <ExternalLink className="ml-2 w-4 h-4 opacity-70" />
              </Link>
            </Button>

            <p className="text-xs text-slate-500 mt-4">
              We'll detect your platform and show the right download
            </p>
          </div>

          {/* Security Features */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Privacy First Architecture</h3>
                <p className="text-slate-400 text-sm">Your credentials never leave your computer</p>
              </div>
            </div>

            {securityFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">{feature.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Trust indicators */}
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>No account required to download</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Virus scanned & notarized</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Auto-updates included</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DownloadSection;
